import React, { useRef, useState } from 'react';
import { styled } from 'styled-components';
import { TextField, Button, Typography, CircularProgress, SvgIcon, Box } from '@mui/material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import '../../styles/Login.css';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import TimeRemaining from '../Timer/TimeRemaining';

//Images
import ChenniLogo from '../../assets/Logo/ylPartsLogo.png';
import ErrorIcon from '../../assets/icons/Danger.png';
import CustomButton from '../CustomButton/CustomButton';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	width: 475px;
	height: 100%;
	@media screen and (max-width: 600px) {
		width: 360px;
		padding: 8px;
	}

	#timer {
		display: flex;
		justify-content: flex-start;
		margin-top: 35px;
		width: 100%;
		@media screen and (max-width: 600px) {
			justify-content: center;
			align-items: center;
		}
	}
`;

const FormContainer = styled.form`
	width: 100%;
	@media screen and (max-width: 600px) {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
	}
`;

const LogoContainer = styled.div`
	width: 100%;
	margin-bottom: 40px;
	@media screen and (max-width: 600px) {
		display: flex;
		justify-content: center;
		align-items: center;
	}
`;

const LogoImage = styled.img`
	height: 40px;
	// width: 190px;
`;

const LoginInfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 100%;
	@media screen and (max-width: 600px) {
		justify-content: center;
		align-items: center;
	}
`;

const Heading = styled(Typography)`
	text-transform: capitalize;
	color: var(--default-text-color);
	text-align: left;
`;

const Paragraph = styled(Typography)`
	font-size: 16px;
	color: var(--lighter-text-color) !important;
	text-align: left;
	padding-left: 3px;
	padding-right: 3px;
	@media screen and (max-width: 600px) {
		text-align: center;
	}
`;

const FieldsContainer = styled.div`
	margin-top: 35px;
	width: 100%;
	height: 110px;

	#otpFields {
		display: flex;
		gap: 12px;
		width: 100%;
		@media screen and (max-width: 600px) {
			justify-content: center;
			align-items: center;
		}
	}
`;

const StyledError = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	color: #f26184;
	margin-top: 16px;
`;

const StyledTextFieldContainer = styled(TextField)`
	input {
		text-align: center;
		font-size: 1.5rem;
		width: 70px;
		height: 48px;
		border-radius: 4px;
		border: 1.5px solid ${(props) => (props.error ? 'red' : '#e9e9e9')};
		background: #fff;

		&:focus {
			border: 1.5px solid ${(props) => (props.error ? 'red' : '#acacac')};
		}
	}
`;

const StyledBox = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`;

interface OTPProps {
	heading: string;
	content: string;
	error: string;
	setError: (data: string) => void;
	setPhoneNumber: (data: string) => void;
	otp: string[];
	setOtp: (data: string[]) => void;
	loading: boolean;
	otpExpiryTime: number;
	setOtpExpiryTime: (data: number) => void;
	handleResend: any;
	handleOTPSubmit: any;
}

const OTP: React.FC<OTPProps> = ({
	heading,
	content,
	error,
	setError,
	setPhoneNumber,
	otp,
	setOtp,
	otpExpiryTime,
	setOtpExpiryTime,
	loading,
	handleResend,
	handleOTPSubmit,
}) => {
	const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

	const handlePhoneNumberChange = (event: string) => {
		setPhoneNumber(event);
		setError('');
	};

	const handleKeyUpChange = (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
		const target = event.target as HTMLInputElement | HTMLTextAreaElement;
		const value = target.value;

		if (
			(event.code === 'Backspace' || event.keyCode === 8) &&
			index > 0 &&
			index <= 3 &&
			value.length === 0
		) {
			otpInputRefs.current[index - 1]?.focus();
		}
	};

	const handleOtpChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		index: number
	) => {
		const value = event.target.value;
		const numberValidate = /^\d*$/.test(value);
		if (numberValidate && value.length <= 1) {
			const newOtp = [...otp];
			newOtp[index] = value;
			setOtp(newOtp);
			setError('');

			if (value.length === 1 && index < 3 && otpInputRefs.current[index + 1]) {
				otpInputRefs.current[index + 1]?.focus();
			} else if (value.length === 0 && index > 0 && otpInputRefs.current[index - 1]) {
				otpInputRefs.current[index - 1]?.focus();
			}
		} else if (numberValidate && value?.length === 2) {
			//move forward only
			if (otp[index + 1]?.length === 0) {
				otpInputRefs.current[index + 1]?.focus();
			}
		}
	};

	return (
		<Container>
			<FormContainer onSubmit={handleOTPSubmit}>
				<LogoContainer>
					<LogoImage src={ChenniLogo} alt="Logo" loading="lazy" />
				</LogoContainer>
				<LoginInfoContainer>
					<Heading variant="h2">{heading}</Heading>
					<Paragraph variant="body1">{content}</Paragraph>
				</LoginInfoContainer>
				<FieldsContainer>
					<div id="otpFields">
						{Array.from({ length: 4 }).map((_, index) => (
							<StyledTextFieldContainer
								variant="standard"
								key={index}
								size="small"
								type="tel"
								placeholder="-"
								error={error !== ''}
								value={otp?.[index] || ''}
								onChange={(e) => handleOtpChange(e, index)}
								onKeyUp={(e) => handleKeyUpChange(e, index)}
								InputProps={{
									disableUnderline: true,
									classes: {
										root: 'MuiInputBase-root',
									},
									inputMode: 'numeric',
								}}
								inputRef={(el) => (otpInputRefs.current[index] = el)}
								autoFocus={index === 0}
							/>
						))}
					</div>
					{error ? (
						<StyledError>
							<img src={ErrorIcon} alt="danger" width="16px" height="16px" />
							<Typography variant="body2">{error}</Typography>
						</StyledError>
					) : null}
				</FieldsContainer>
				<CustomButton
					type="submit"
					title={
						loading ? (
							<CircularProgress size={20} style={{ color: 'var(--default-text-color)' }} />
						) : (
							'Continue'
						)
					}
					handleClick={handleOTPSubmit}
					color="primary"
					styles={{
						width: '148px',
						height: '36px',
						'@media screen and (max-width: 600px)': {
							width: '326px',
						},
					}}
					disabled={otp.join('').length < 4 || loading}
				/>
			</FormContainer>
			<div id="timer">
				<Typography
					variant="body1"
					style={{ display: 'flex', gap: '5px' }}
					className="default-lighter-text"
				>
					<TimeRemaining expiryTime={otpExpiryTime} handleResend={handleResend} />
				</Typography>
			</div>
		</Container>
	);
};

export default OTP;
